/**
 * Typed fetch wrapper around the API. Every call includes credentials (the
 * httpOnly session cookie), sends and parses JSON, and turns any error response
 * into an `ApiError` that carries the shared `ErrorCode` and the friendly
 * `kidMessage` the UI shows to students.
 */

import {
  DEFAULT_KID_MESSAGES,
  isErrorCode,
  type ApiErrorResponse,
  type ErrorCode,
} from '@proteam/shared';

/** Base path for API calls. Dev proxies `/api` to the API service on 4300. */
const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

/** An error raised by any API call. Carries the code and friendly message. */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly kidMessage: string;
  readonly status: number;
  readonly resetsAt: string | undefined;

  constructor(status: number, code: ErrorCode, message: string, kidMessage: string, resetsAt?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.kidMessage = kidMessage;
    this.resetsAt = resetsAt;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/** Narrow an unknown JSON body to the shared ApiErrorResponse shape. */
function asErrorResponse(body: unknown): ApiErrorResponse | null {
  if (typeof body !== 'object' || body === null) return null;
  const err = (body as { error?: unknown }).error;
  if (typeof err !== 'object' || err === null) return null;
  const { code, message, kidMessage } = err as Record<string, unknown>;
  if (typeof code !== 'string' || !isErrorCode(code)) return null;
  return {
    error: {
      code,
      message: typeof message === 'string' ? message : code,
      kidMessage: typeof kidMessage === 'string' ? kidMessage : DEFAULT_KID_MESSAGES[code],
    },
  };
}

/** Best guess at an error code when the body is missing or malformed. */
function codeForStatus(status: number): ErrorCode {
  switch (status) {
    case 400:
      return 'bad_request';
    case 401:
      return 'unauthorized';
    case 402:
      return 'payment_required';
    case 403:
      return 'forbidden';
    case 404:
      return 'not_found';
    case 409:
      return 'conflict';
    case 422:
      return 'validation_error';
    case 429:
      return 'rate_limited';
    case 503:
      return 'not_configured';
    default:
      return 'internal';
  }
}

async function toApiError(res: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  const parsed = asErrorResponse(body);
  if (parsed) {
    // Some endpoints attach a resetsAt alongside the standard error object.
    const resetsAt = (body as { error?: { resetsAt?: unknown } }).error?.resetsAt;
    return new ApiError(
      res.status,
      parsed.error.code,
      parsed.error.message,
      parsed.error.kidMessage,
      typeof resetsAt === 'string' ? resetsAt : undefined,
    );
  }
  const code = codeForStatus(res.status);
  return new ApiError(res.status, code, `Request failed with status ${res.status}`, DEFAULT_KID_MESSAGES[code]);
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** JSON body (any serializable value) or a FormData for uploads. */
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

/** Perform a request and return the parsed JSON body (or undefined for 204). */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, signal } = options;
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;

  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(body !== undefined && !isForm ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
  };
  if (body !== undefined) init.body = isForm ? (body as FormData) : JSON.stringify(body);
  if (signal) init.signal = signal;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, init);
  } catch {
    // Network failure: no response at all (offline, DNS, CORS). Present a
    // friendly connection message rather than a raw fetch error.
    throw new ApiError(
      0,
      'internal',
      'Network request failed',
      'We could not reach the server. Check your connection and try again.',
    );
  }

  if (!res.ok) throw await toApiError(res);

  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

/** Convenience verbs over `request`. */
export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  del: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
